import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';

import { HttpService } from './http.service';
import { LocalstorageService } from './localstorage.service';
import type { PermissionName } from './permissions.generated';

/**
 * Permission names exactly as the API defines them, generated from the
 * scripay-middleware catalogue (`python manage.py export_permissions`).
 * Every endpoint needs one, reads included: `<resource>.view` to see a page,
 * the other actions to change anything on it.
 */
export { PERMISSIONS, CRITICAL_PERMISSIONS, LEGACY_PERMISSIONS, PERMISSION_INFO } from './permissions.generated';
export type { PermissionName } from './permissions.generated';

interface StoredUserObject {
  permissions?: { permission__name: string }[];
  [key: string]: unknown;
}

interface MyPermissions {
  permissions: string[];
  merchant: number | null;
  platform: boolean;
  step_up_fresh: boolean;
}

/**
 * The signed-in user's permissions. Seeded from the login response and
 * refreshed from `authorization/my-permissions`, so a role change made by an
 * admin applies on the next page load without signing in again.
 *
 * Hiding something here is a convenience only — the API enforces every
 * permission itself.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly http = inject(HttpService);
  private readonly storage = inject(LocalstorageService);

  private readonly granted = signal<ReadonlySet<string>>(this.fromStorage());

  /** All permission names the user holds. */
  readonly permissions = computed(() => [...this.granted()].sort());

  /** Scripay staff act across every merchant; set from my-permissions. */
  readonly platform = signal(false);

  /** A one-time code was confirmed recently, so critical changes go straight through. */
  readonly stepUpFresh = signal(false);

  has(permission: PermissionName | string): boolean {
    return this.granted().has(permission);
  }

  /** True when the user holds at least one of the permissions (or none are required). */
  hasAny(permissions: readonly (PermissionName | string)[] | null | undefined): boolean {
    if (!permissions || permissions.length === 0) return true;
    const granted = this.granted();
    return permissions.some(p => granted.has(p));
  }

  /** Reload from the API; keeps the current set if the request fails. */
  refresh(): Observable<string[]> {
    return this.http
      .get<MyPermissions>('authorization/my-permissions', { skipErrorNotification: true })
      .pipe(
        map((response: any) => (response?.body ?? response) as MyPermissions | undefined),
        tap(data => {
          if (!data) return;
          if (Array.isArray(data.permissions)) this.set(data.permissions);
          this.platform.set(!!data.platform);
          this.stepUpFresh.set(!!data.step_up_fresh);
        }),
        map(data => data?.permissions ?? this.permissions()),
        catchError(() => of(this.permissions()))
      );
  }

  /** Re-read the stored login response (e.g. right after signing in). */
  reloadFromStorage(): void {
    this.granted.set(this.fromStorage());
  }

  private set(names: string[]): void {
    this.granted.set(new Set(names));
    const user = this.storage.get<StoredUserObject>('userObject');
    if (user) {
      this.storage.set('userObject', { ...user, permissions: names.map(name => ({ permission__name: name })) });
    }
  }

  private fromStorage(): ReadonlySet<string> {
    const user = this.storage.get<StoredUserObject>('userObject');
    return new Set((user?.permissions ?? []).map(p => p?.permission__name).filter(Boolean) as string[]);
  }
}
